import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { translateMessage } from "@delibrandev/utils";

import {
  createOption,
  createOptionGroup,
  getOptionGroups,
  updateOption,
  updateOptionGroup,
  addOptionsGroupToProduct,
} from "../../../../../../../api/options";

import { getProduct } from "../../../../../../../api/products";
import { setAlert, updateCorporationData, updateFloatPanelData } from "../../../../../../../reducers/userConfigSlice";

import { buildOptionIdsForProduct, computeNewCombinations } from "../utils/optionsHandlers.utils";

export const useOptionsHandler = (initialGroupId = null) => {
  const dispatch = useDispatch();
  const limitVariants = 100;

  const idProduct = useSelector((state) => state.aplicationConfig.userConfig.floatPanel.queryId);
  const allGroupOptions = useSelector((state) => state.aplicationConfig.corporationData.options);
  const optionsProduct = useSelector((state) => state.aplicationConfig.userConfig.floatPanel.data.options);

  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId);

  // Drafts
  const [groupNameDraft, setGroupNameDraft] = useState("");
  const [isMainGroupDraft, setIsMainGroupDraft] = useState(false);
  const [optionDrafts, setOptionDrafts] = useState({}); // { [optionId]: "name" }

  const groupActive = useMemo(() => {
    if (!selectedGroupId) return null;
    return allGroupOptions.find((g) => g.optionGroupId === selectedGroupId) || null;
  }, [allGroupOptions, selectedGroupId]);

  // Inicializar selección
  useEffect(() => {
    if (!selectedGroupId && allGroupOptions?.length > 0) {
      setSelectedGroupId(allGroupOptions[0].optionGroupId);
    }
  }, [allGroupOptions, selectedGroupId]);

  // Sync drafts al cambiar de grupo
  useEffect(() => {
    if (!groupActive) return;

    setGroupNameDraft(groupActive.name || "");
    setIsMainGroupDraft(!!groupActive.isMainGroup);

    const next = {};
    (groupActive.options || []).forEach((opt) => {
      next[opt.optionId] = opt.name || "";
    });
    setOptionDrafts(next);
  }, [groupActive]);

  const alertFromResponse = (response) => {
    dispatch(setAlert(translateMessage(response?.message || "Bad request")));
  };

  const queryOptions = async (keepSelected = true) => {
    const responseOptions = await getOptionGroups();

    if (responseOptions && !responseOptions.error) {
      dispatch(updateCorporationData({ key: "options", data: responseOptions.data }));
      if (!keepSelected && responseOptions.data?.length) {
        setSelectedGroupId(responseOptions.data[0].optionGroupId);
      }
      return true;
    }

    dispatch(setAlert(translateMessage(responseOptions?.message || "Bad request")));
    return false;
  };

  const refreshVariantsProduct = async () => {
    const response = await getProduct(idProduct);

    if (response && !response.error) {
      dispatch(updateFloatPanelData({ data: response.data.variants, name: "variants" }));
      dispatch(updateFloatPanelData({ data: response.data.options, name: "options" }));
      return true;
    }

    dispatch(setAlert(translateMessage(response?.message || "Bad request")));
    return false;
  };

  const newGroup = async () => {
    const payload = { name: "Nuevo grupo" };
    const response = await createOptionGroup(payload);

    if (response && !response.error) {
      alertFromResponse(response);
      await queryOptions(false);
      return;
    }
    alertFromResponse(response);
  };

  const saveGroupName = async () => {
    if (!groupActive) return;

    const payload = { name: groupNameDraft?.trim() || "Sin nombre", isMainGroup: !!isMainGroupDraft };
    const response = await updateOptionGroup(payload, groupActive.optionGroupId);

    if (response && !response.error) {
      alertFromResponse(response);
      await queryOptions(true);
      return;
    }
    alertFromResponse(response);
  };

  const newOption = async () => {
    if (!groupActive) return;

    const payload = { name: "Nueva opción", optionGroupId: groupActive.optionGroupId };
    const response = await createOption(payload, groupActive.optionGroupId);

    if (response && !response.error) {
      alertFromResponse(response);
      await queryOptions(true);
      return;
    }
    alertFromResponse(response);
  };

  const saveOptionName = async (optionId) => {
    if (!groupActive || !optionId) return;

    const payload = { name: (optionDrafts[optionId] || "").trim() || "Sin nombre" };
    const response = await updateOption(payload, groupActive.optionGroupId, optionId);

    if (response && !response.error) {
      alertFromResponse(response);
      await queryOptions(true);
      return;
    }
    alertFromResponse(response);
  };

  const addGroupToProduct = async () => {
    if (!groupActive) return;

    const optionIds = buildOptionIdsForProduct(optionsProduct, groupActive);
    const newCombinations = computeNewCombinations(optionsProduct, groupActive);

    if (newCombinations > limitVariants) {
      dispatch(
        setAlert(
          translateMessage(
            `No es posible agregar este grupo de opciones porque las variaciones (${newCombinations}) superarían el límite (${limitVariants})`,
          ),
        ),
      );
      return;
    }

    // Debe usar el endpoint admin para añadir grupos/items al producto
    const response = await addOptionsGroupToProduct(idProduct, { optionIds });
    if (response && !response.error) {
      alertFromResponse(response);
      await refreshVariantsProduct();
      return;
    }
    alertFromResponse(response);
  };

  return {
    // state
    allGroupOptions,
    selectedGroupId,
    groupActive,
    groupNameDraft,
    optionDrafts,
    isMainGroupDraft,

    // setters
    setSelectedGroupId,
    setGroupNameDraft,
    setOptionDrafts,
    setIsMainGroupDraft,

    // actions
    newGroup,
    saveGroupName,
    newOption,
    saveOptionName,
    addGroupToProduct,
  };
};
